import React, { useState, useEffect, useContext } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import axios from "axios";

import MDBox from "components/MDBox";
import TypeBadge from "components/ReportUI/Typebadge";
import MDButton from "components/MDButton";
import DownloadExcelButton from "components/ReportUI/Downloadexcelbutton";
import MDSnackbar from "components/MDSnackbar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { AccountsContext } from "context/AccountsContext";

import AttendanceReportTable from "./attendanceReportTable";
import { attendancereportendpoint } from "config";
import SingleDatePicker from "layouts/SingleDatePicker/singleDatePicker";
import DhundhooLoader from "../Loader/Loader";

function AttendanceReport() {
  const [reportData, setReportData] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [successSB, setSuccessSB] = useState(false);
  const [errorSB, setErrorSB] = useState(false);
  const [vehicleId, setVehicleId] = useState("");
  const [loading, setLoading] = useState(false);

  const getTodayDateString = () => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const defaultDate = getTodayDateString();

  // ✅ Set to null by default for placeholder
  const [date, setDate] = useState(defaultDate);

  const { fetchThings, things } = useContext(AccountsContext);

  useEffect(() => {
    fetchThings();
  }, []);

  // Snackbar
  const openSuccessSB = () => setSuccessSB(true);
  const closeSuccessSB = () => setSuccessSB(false);
  const openErrorSB = () => setErrorSB(true);
  const closeErrorSB = () => setErrorSB(false);

  // Columns for Attendance Report
  const columns = [
    { Header: "Vehicle No", accessor: "thingId" },
    { Header: "Vehicle Name", accessor: "thingName" },
    {
      Header: "Journey Type", accessor: "journeyName",
      Cell: ({ value }) => (
        <TypeBadge
          value={value}
          config={{}}
          defaultConfig={{ color: "#1E40AF", bg: "#E9F1FF", icon: null }}
        />
      ),
    },
    { Header: "Account Name", accessor: "accountName" },
    { Header: "Account Role", accessor: "accountRole" },
    { Header: "Access Id", accessor: "accessId" },
    { Header: "Time Stamp", accessor: "timeStamp" },
    {
      Header: "Location",
      accessor: "location",
      Cell: (cell) => {
        const value = cell.value;
        return value ? (
          <a href={value} target="_blank" rel="noopener noreferrer">
            📍
          </a>
        ) : (
          "-"
        );
      },
    },
  ];

  // ✅ New Formatter: Converts "YYYY-MM-DD" to "YYYY/MM/DD"
  const formatApiDate = (dateString) => {
    if (!dateString) return "";
    return dateString.replace(/-/g, "/");
  };

  const fetchReport = async () => {
    const selectedThingIds = vehicleId
      ? [vehicleId]
      : (things || []).map((item) => item.thingId);

    // ✅ Validation to ensure date is picked
    if (!date) {
      setErrorMessage("Please select a date first");
      openErrorSB();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    setErrorMessage("");

    try {
      const formattedDate = formatApiDate(date); // Convert to YYYY/MM/DD

      const data = {
        thingIds: selectedThingIds,
        timeZone: "Asia/Kolkata",
        startTime: `${formattedDate} 00:00`,
        endTime: `${formattedDate} 23:59`,
      };

      const config = {
        method: "post",
        url: attendancereportendpoint, // API endpoint
        headers: {
          "x-dhundhoo-session": token,
          "Content-Type": "application/json",
        },
        data,
      };

      const response = await axios.request(config);
      const formattedData = (response.data.rowList || []).map((item) => ({
        ...item,

        // Same value shown in table + CSV
        journeyName: `${item.journeyName || ""}`,
        location: `${item.location || ""}`
      }));
      setReportData(formattedData);
      openSuccessSB();
    } catch (error) {
      console.error(error);
      setErrorMessage("Error loading attendance report");
      openErrorSB();
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setVehicleId("");
    setReportData([]);
    setErrorMessage("");
    setDate(defaultDate); // ✅ Reset back to today's date
  };

  const exportToCSV = () => {
    if (reportData.length === 0) {
      setErrorMessage("No data to export");
      openErrorSB();
      return;
    }

    const headers = [
      "Vehicle No",
      "Vehicle Name",
      "Journey Type",
      "Account Name",
      "Account Role",
      "Access Id",
      "Time Stamp",
      "Location",
    ];

    const rows = reportData.map((row) =>
      [
        row.thingId,
        row.thingName,

        // Same data as table UI
        `"${row.journeyName || ""}"`,

        row.accountName,
        row.accountRole,
        row.accessId,
        row.timeStamp,
        `"${row.location || ""}"`,
      ].join(",")
    );

    const exportDate = date
      ? date.split("-").reverse().join("-")
      : "export";

    const csvContent = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `AttendanceReport_${exportDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox p={3} pb={0}>
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <Grid container spacing={2}>
                    {/* Vehicle Select */}
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={things || []}
                        getOptionLabel={(option) => option.thingId || ""}
                        filterOptions={(options, { inputValue }) => {
                          const searchValue = inputValue.toLowerCase().trim();

                          return options.filter((option) => {
                            const name = (option.name || "").toLowerCase();
                            const nickName = (option.nickName || "").toLowerCase();
                            const thingId = (option.thingId || "").toLowerCase();

                            return (
                              name.includes(searchValue) ||
                              nickName.includes(searchValue) ||
                              thingId.includes(searchValue)
                            );
                          });
                        }}
                        value={
                          things.find((t) => t.thingId === vehicleId) || null
                        }
                        onChange={(event, newValue) => {
                          setVehicleId(newValue ? newValue.thingId : "");
                        }}
                        renderOption={(props, option) => (
                          <li
                            {...props}
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              width: "100%",
                            }}
                          >
                            <span>{option.nickName || option.name || "-"}</span>
                            <span style={{ color: "#666" }}>
                              {option.thingId}
                            </span>
                          </li>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            placeholder="Select Vehicle"
                            fullWidth
                            size="small"
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                  borderColor: "#FB8C00",
                                },
                              },
                              "& .MuiInputLabel-root.Mui-focused": {
                                color: "#FB8C00",
                              },
                            }}
                          />
                        )}
                      />
                    </Grid>

                    {/* ✅ Replaced DatePicker with SingleDatePicker */}
                    <Grid item xs={12} sm={6} md={3}>
                      <div style={{ width: '70%' }}>
                        <SingleDatePicker
                          date={date}
                          onChange={(newDate) => setDate(newDate)}
                          disabled={loading}
                        />
                      </div>
                    </Grid>

                    {/* Buttons */}
                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        fullWidth
                        variant="gradient"
                        color="warning"
                        size="small"
                        onClick={fetchReport}
                        disabled={loading}
                      >
                        {loading ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          "LOAD"
                        )}
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <MDButton
                        fullWidth
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={clearFilters}
                        disabled={loading}
                      >
                        CLEAR
                      </MDButton>
                    </Grid>

                    <Grid item xs={4} sm={2} md={1}>
                      <DownloadExcelButton fullWidth label="CSV" onClick={exportToCSV} disabled={loading || reportData.length === 0} />
                    </Grid>
                  </Grid>
                </LocalizationProvider>
              </MDBox>

              {/* Error Alert */}
              {errorMessage && (
                <MDBox px={2} pt={2}>
                  <Alert severity="error">{errorMessage}</Alert>
                </MDBox>
              )}

              {/* Loader or Table */}
              <MDBox pt={3} display="flex" justifyContent="center">
                {loading ? (
                  <DhundhooLoader />
                ) : (
                  <AttendanceReportTable
                    table={{ columns, rows: reportData }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Snackbars */}
      <MDSnackbar
        color="success"
        icon="check"
        title="Attendance Report"
        content="Data Loaded Successfully"
        open={successSB}
        onClose={closeSuccessSB}
        close={closeSuccessSB}
        bgWhite
      />
      <MDSnackbar
        color="error"
        icon="warning"
        title="Attendance Report"
        content="Failed to load report"
        open={errorSB}
        onClose={closeErrorSB}
        close={closeErrorSB}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default AttendanceReport;